/* Video carousel block — scroll, drag, rotate previews, fullscreen player */
if (!customElements.get('video-carousel')) {
  class VideoCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.video-carousel__track');
      this.cards = Array.from(this.querySelectorAll('.video-carousel__card'));
      if (!this.track || !this.cards.length) return;

      this.videos = this.cards.map((card) => card.querySelector('video'));
      this.mode = this.dataset.previewMode || 'rotate';
      this.rotateMs = (parseInt(this.dataset.rotateSeconds, 10) || 5) * 1000;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.current = 0;
      this.inView = false;
      this.dragging = false;

      this.setupArrows();
      this.setupDots();
      this.setupDrag();
      this.setupCards();
      this.setupModal();

      this.onScroll = this.onScroll.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });

      this.io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.inView = entry.isIntersecting;
            if (entry.isIntersecting) {
              this.loadPreviews();
              this.startPreview();
            } else {
              this.stopPreview();
            }
          });
        },
        { threshold: 0.2 }
      );
      this.io.observe(this);

      this.onVisibility = () => {
        if (document.hidden) this.stopPreview();
        else if (this.inView) this.startPreview();
      };
      document.addEventListener('visibilitychange', this.onVisibility);

      this.onResize = () => this.updateArrows();
      window.addEventListener('resize', this.onResize);
      this.updateArrows();
    }

    disconnectedCallback() {
      this.stopPreview();
      if (this.io) this.io.disconnect();
      document.removeEventListener('visibilitychange', this.onVisibility);
      window.removeEventListener('resize', this.onResize);
      if (this.modal) {
        this.closeModal(true);
        this.modal.remove();
      }
    }

    /* ---------- previews ---------- */

    /* Attach a source only when a card is (nearly) visible or about to play. */
    loadPreview(index) {
      const video = this.videos[index];
      const card = this.cards[index];
      if (!video || video.tagName !== 'VIDEO' || this.mode === 'none') return null;
      if (!video._ready) {
        video.preload = 'metadata';
        video._ready = VideoCarousel.attachSource(video, card.dataset.hls, card.dataset.mp4);
      }
      return video._ready;
    }

    /* Cards: HLS via hls.js (size-capped, adaptive) → native HLS → MP4.
       Fullscreen (preferMp4): the largest MP4 directly.
       Returns a promise that resolves when a source is attached. */
    static attachSource(video, hls, mp4, preferMp4) {
      if (video._hls) {
        video._hls.destroy();
        video._hls = null;
      }
      // Fullscreen: play the top-quality MP4 outright (no adaptive ramp-up
      // from a small rendition), same as Honeylove's player.
      if (preferMp4 && mp4) {
        video.src = mp4;
        return Promise.resolve();
      }
      const nativeHls = hls && video.canPlayType('application/vnd.apple.mpegurl') !== '';
      const fallback = () => {
        if (nativeHls) video.src = hls;
        else if (mp4) video.src = mp4;
      };
      // Prefer hls.js wherever Media Source Extensions exist: it caps the
      // rendition to the player size (small cards get 480p, fullscreen gets
      // 1080p). iOS Safari has no MSE and plays HLS natively instead.
      if (hls && window.MediaSource) {
        return VideoCarousel.loadHlsJs().then((Hls) => {
          if (Hls && Hls.isSupported()) {
            const instance = new Hls({ capLevelToPlayerSize: true, startLevel: 0, maxBufferLength: 10 });
            instance.loadSource(hls);
            instance.attachMedia(video);
            instance.on(Hls.Events.ERROR, (event, data) => {
              if (data.fatal) {
                instance.destroy();
                video._hls = null;
                fallback();
              }
            });
            video._hls = instance;
          } else {
            fallback();
          }
        });
      }
      fallback();
      return Promise.resolve();
    }

    static loadHlsJs() {
      if (window.Hls) return Promise.resolve(window.Hls);
      if (!VideoCarousel._hlsPromise) {
        VideoCarousel._hlsPromise = new Promise((resolve) => {
          const script = document.createElement('script');
          script.src = 'https://cdn.jsdelivr.net/npm/hls.js@1/dist/hls.min.js';
          script.async = true;
          script.onload = () => resolve(window.Hls || null);
          script.onerror = () => resolve(null);
          document.head.appendChild(script);
        });
      }
      return VideoCarousel._hlsPromise;
    }

    loadPreviews() {
      if (this.mode === 'none') return;
      // 'first' (Tala style): only the first video previews; the rest show their poster and play icon.
      if (this.mode === 'first') {
        if (this.firstVideo() >= 0) this.loadPreview(this.firstVideo());
        return;
      }
      const rect = this.track.getBoundingClientRect();
      this.cards.forEach((card, i) => {
        const r = card.getBoundingClientRect();
        if (r.right > rect.left - r.width && r.left < rect.right + r.width) this.loadPreview(i);
      });
    }

    playPreview(index, scroll) {
      const video = this.videos[index];
      if (!video || video.tagName !== 'VIDEO') return;
      const ready = this.loadPreview(index) || Promise.resolve();
      this.loadPreview((index + 1) % this.cards.length);
      this.cards[index].classList.add('is-playing');
      ready.then(() => {
        if (!this.cards[index].classList.contains('is-playing')) return;
        const promise = video.play();
        if (promise) promise.catch(() => this.cards[index].classList.remove('is-playing'));
      });
      if (scroll && !this.dragging) this.scrollToCard(index);
    }

    pausePreview(index, reset) {
      const video = this.videos[index];
      if (!video || video.tagName !== 'VIDEO') return;
      video.pause();
      if (reset) {
        try {
          video.currentTime = 0;
        } catch (e) {
          /* not loaded yet */
        }
      }
      this.cards[index].classList.remove('is-playing');
    }

    startPreview() {
      if (this.modalOpen || this.reduceMotion) return;
      if (this.mode === 'rotate') {
        this.stopRotate();
        this.playPreview(this.current, true);
        this.rotateTimer = setInterval(() => {
          this.pausePreview(this.current, true);
          this.current = (this.current + 1) % this.cards.length;
          this.playPreview(this.current, true);
        }, this.rotateMs);
      } else if (this.mode === 'all') {
        this.playVisible();
      } else if (this.mode === 'first') {
        if (this.firstVideo() >= 0) this.playPreview(this.firstVideo(), false);
      }
    }

    // Photo tiles have no <video>: 'first' previews the first tile that is a video.
    firstVideo() {
      return this.videos.findIndex((video) => video && video.tagName === 'VIDEO');
    }

    stopPreview() {
      this.stopRotate();
      this.videos.forEach((v, i) => this.pausePreview(i, this.mode === 'rotate'));
    }

    stopRotate() {
      if (this.rotateTimer) {
        clearInterval(this.rotateTimer);
        this.rotateTimer = null;
      }
    }

    playVisible() {
      const rect = this.track.getBoundingClientRect();
      this.cards.forEach((card, i) => {
        const r = card.getBoundingClientRect();
        const visible = r.right > rect.left + 4 && r.left < rect.right - 4;
        if (visible) this.playPreview(i, false);
        else this.pausePreview(i, false);
      });
    }

    /* ---------- scrolling ---------- */

    scrollToCard(index, behavior) {
      const card = this.cards[index];
      if (!card) return;
      const left = card.offsetLeft;
      const right = left + card.offsetWidth;
      const viewLeft = this.track.scrollLeft;
      const viewRight = viewLeft + this.track.clientWidth;
      if (left >= viewLeft && right <= viewRight) return;
      this.track.scrollTo({ left, behavior: behavior || (this.reduceMotion ? 'auto' : 'smooth') });
    }

    setupArrows() {
      this.arrows = Array.from(this.querySelectorAll('.video-carousel__arrow'));
      this.arrows.forEach((arrow) => {
        arrow.addEventListener('click', () => {
          const dir = parseInt(arrow.dataset.dir, 10) || 1;
          const step = this.cards[0].offsetWidth + this.gap();
          const perPage = Math.max(1, Math.floor(this.track.clientWidth / step));
          this.track.scrollBy({ left: dir * step * perPage, behavior: this.reduceMotion ? 'auto' : 'smooth' });
        });
      });
    }

    gap() {
      const value = getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || '0';
      return parseFloat(value) || 0;
    }

    updateArrows() {
      if (!this.arrows || !this.arrows.length) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const left = this.track.scrollLeft;
      this.arrows.forEach((arrow) => {
        const dir = parseInt(arrow.dataset.dir, 10) || 1;
        const atEdge = dir < 0 ? left <= 1 : left >= max - 1;
        arrow.toggleAttribute('disabled', atEdge || max <= 1);
      });
    }

    setupDots() {
      this.dots = Array.from(this.querySelectorAll('.video-carousel__dot'));
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index, 10) || 0;
          this.track.scrollTo({ left: this.cards[index].offsetLeft, behavior: this.reduceMotion ? 'auto' : 'smooth' });
        });
      });
    }

    updateDots() {
      if (!this.dots.length) return;
      const step = this.cards[0].offsetWidth + this.gap();
      const max = this.track.scrollWidth - this.track.clientWidth;
      let index = Math.round(this.track.scrollLeft / step);
      if (this.track.scrollLeft >= max - 1) index = this.cards.length - 1;
      index = Math.min(this.cards.length - 1, Math.max(0, index));
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
    }

    onScroll() {
      this.updateArrows();
      this.updateDots();
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.loadPreviews();
        if (this.mode === 'all' && this.inView && !this.modalOpen) this.playVisible();
      }, 120);
    }

    /* Mouse drag-to-scroll. Touch already scrolls natively. */
    setupDrag() {
      let startX = 0;
      let startLeft = 0;
      let moved = false;
      let pointerId = null;

      this.track.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        pointerId = event.pointerId;
        startX = event.clientX;
        startLeft = this.track.scrollLeft;
        moved = false;
      });

      this.track.addEventListener('pointermove', (event) => {
        if (pointerId === null || event.pointerId !== pointerId) return;
        const dx = event.clientX - startX;
        if (!moved && Math.abs(dx) > 6) {
          moved = true;
          this.dragging = true;
          this.track.classList.add('is-dragging');
          this.track.setPointerCapture(pointerId);
        }
        if (moved) {
          this.track.scrollLeft = startLeft - dx;
          event.preventDefault();
        }
      });

      const end = (event) => {
        if (pointerId === null || (event && event.pointerId !== pointerId)) return;
        pointerId = null;
        if (moved) {
          setTimeout(() => {
            this.dragging = false;
            this.track.classList.remove('is-dragging');
          }, 0);
        }
      };
      this.track.addEventListener('pointerup', end);
      this.track.addEventListener('pointercancel', end);
      this.track.addEventListener('lostpointercapture', end);
    }

    setupCards() {
      this.cards.forEach((card, index) => {
        card.addEventListener('click', () => {
          if (this.dragging) return;
          this.openModal(index);
        });
        if (this.mode === 'hover') {
          card.addEventListener('mouseenter', () => this.playPreview(index, false));
          card.addEventListener('mouseleave', () => this.pausePreview(index, true));
        }
      });
    }

    /* ---------- fullscreen player ---------- */

    setupModal() {
      this.modal = this.querySelector('.video-carousel-modal');
      if (!this.modal) return;
      document.body.appendChild(this.modal);

      this.modalVideo = this.modal.querySelector('.video-carousel-modal__video');
      this.modalImage = this.modal.querySelector('.video-carousel-modal__image');
      this.modalCaption = this.modal.querySelector('.video-carousel-modal__caption');
      this.modalProgress = this.modal.querySelector('.video-carousel-modal__progress span');
      this.modalDots = Array.from(this.modal.querySelectorAll('.video-carousel-modal__dot'));
      this.muteButton = this.modal.querySelector('.video-carousel-modal__mute');
      this.modalOpen = false;
      this.modalIndex = 0;

      this.modal.querySelector('.video-carousel-modal__close').addEventListener('click', () => this.closeModal());
      this.modal.querySelectorAll('.video-carousel-modal__nav').forEach((button) => {
        button.addEventListener('click', () => this.stepModal(parseInt(button.dataset.dir, 10) || 1));
      });
      this.modalDots.forEach((dot) => {
        dot.addEventListener('click', () => this.showModalVideo(parseInt(dot.dataset.index, 10) || 0));
      });
      this.modal.querySelector('.video-carousel-modal__toggle').addEventListener('click', () => this.togglePlay());
      this.muteButton.addEventListener('click', () => {
        this.modalVideo.muted = !this.modalVideo.muted;
        this.updateMute();
      });

      this.modalVideo.addEventListener('ended', () => this.stepModal(1));
      this.modalVideo.addEventListener('timeupdate', () => {
        if (!this.modalVideo.duration) return;
        this.modalProgress.style.width = `${(this.modalVideo.currentTime / this.modalVideo.duration) * 100}%`;
      });
      this.modalVideo.addEventListener('play', () => this.modal.classList.remove('is-paused'));
      this.modalVideo.addEventListener('pause', () => this.modal.classList.add('is-paused'));
      this.modalVideo.addEventListener('volumechange', () => this.updateMute());

      this.onKey = (event) => {
        if (!this.modalOpen) return;
        if (event.key === 'Escape') this.closeModal();
        else if (event.key === 'ArrowRight') this.stepModal(1);
        else if (event.key === 'ArrowLeft') this.stepModal(-1);
        else if (event.key === ' ') {
          event.preventDefault();
          this.togglePlay();
        }
      };

      let touchX = null;
      this.modal.addEventListener('touchstart', (event) => (touchX = event.touches[0].clientX), { passive: true });
      this.modal.addEventListener(
        'touchend',
        (event) => {
          if (touchX === null) return;
          const dx = event.changedTouches[0].clientX - touchX;
          touchX = null;
          if (Math.abs(dx) > 50) this.stepModal(dx < 0 ? 1 : -1);
        },
        { passive: true }
      );
    }

    openModal(index) {
      if (!this.modal) return;
      this.stopPreview();
      this.modalOpen = true;
      this.returnFocus = this.cards[index];
      document.body.classList.add('video-carousel-open');
      this.modal.hidden = false;
      document.addEventListener('keydown', this.onKey);
      this.modalVideo.muted = false;
      this.showModalVideo(index);
      this.modal.querySelector('.video-carousel-modal__close').focus({ preventScroll: true });
    }

    showModalVideo(index) {
      const card = this.cards[index];
      if (!card) return;
      this.modalIndex = index;
      this.modalVideo.pause();
      this.modalVideo.poster = card.dataset.poster || '';
      this.modalVideo.removeAttribute('src');
      this.modalProgress.style.width = '0%';
      const caption = card.dataset.caption || '';
      this.modalCaption.textContent = caption;
      this.modalCaption.hidden = caption === '';
      this.modalDots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

      // Photo tile: show the photo, no playback controls.
      const isImage = !!card.dataset.image && !card.dataset.hls && !card.dataset.mp4;
      this.modal.classList.toggle('is-image', isImage);
      if (this.modalImage) {
        this.modalImage.hidden = !isImage;
        if (isImage) this.modalImage.src = card.dataset.image;
        else this.modalImage.removeAttribute('src');
      }
      if (isImage) {
        if (this.modalVideo._hls) {
          this.modalVideo._hls.destroy();
          this.modalVideo._hls = null;
        }
        this.modalVideo.removeAttribute('poster');
        return;
      }

      VideoCarousel.attachSource(this.modalVideo, card.dataset.hls, card.dataset.mp4, true).then(() => {
        if (!this.modalOpen || this.modalIndex !== index) return;
        const promise = this.modalVideo.play();
        if (promise) {
          promise.catch(() => {
            // Browser refused sound: fall back to muted playback, user can unmute.
            this.modalVideo.muted = true;
            this.updateMute();
            this.modalVideo.play().catch(() => {});
          });
        }
      });
      this.updateMute();
    }

    stepModal(dir) {
      const n = this.cards.length;
      this.showModalVideo((this.modalIndex + dir + n) % n);
    }

    togglePlay() {
      if (this.modalVideo.paused) this.modalVideo.play().catch(() => {});
      else this.modalVideo.pause();
    }

    updateMute() {
      if (this.muteButton) this.muteButton.setAttribute('aria-pressed', this.modalVideo.muted ? 'true' : 'false');
    }

    closeModal(silent) {
      if (!this.modal || !this.modalOpen) return;
      this.modalOpen = false;
      this.modalVideo.pause();
      if (this.modalVideo._hls) {
        this.modalVideo._hls.destroy();
        this.modalVideo._hls = null;
      }
      this.modalVideo.removeAttribute('src');
      this.modalVideo.load();
      this.modal.hidden = true;
      document.body.classList.remove('video-carousel-open');
      document.removeEventListener('keydown', this.onKey);
      if (!silent) {
        if (this.returnFocus) this.returnFocus.focus({ preventScroll: true });
        if (this.inView) this.startPreview();
      }
    }
  }

  customElements.define('video-carousel', VideoCarousel);
}
